/**
 * The OAuth callback's note to the page the user lands on.
 *
 * Why the callback page cannot report its own success: production clerk-js
 * 5.128.0 `setActive` (read off the served bundle, 2026-10-05) clears the
 * session into a transitive state, awaits a FULL-PAGE navigate to the redirect
 * target, and returns while the page is unloading before the hydrated session is
 * ever emitted. So AuthCallbackPage's poll and Clerk listener can never observe
 * the session, and `auth_callback_completed` captured there reached PostHog for
 * 2 of 46 successful callbacks over 2026-09-05..10-05 (48 entered, 46 landed
 * signed in within 5 minutes, 3 failed).
 *
 * The honest observer is the landing document: ProtectedRoute owns Clerk's real
 * isLoaded transition there (the same reasoning that already puts
 * `auth_load_succeeded` on it). The callback page leaves this note at entry and
 * clears it on every outcome it CAN see (in-page completion, failure, loop
 * abort); ProtectedRoute consumes it once Clerk hydrates signed-in.
 *
 * sessionStorage, deliberately: it survives the full-page navigation, is scoped
 * to the tab, and dies with it. The TTL exists because a note that is never
 * consumed (bounce to /sign-in, tab left open) must not claim an unrelated
 * sign-in hours later as a completed callback; 5 minutes is the same window the
 * 2026-10-05 PostHog join used to count landings.
 */
export const AUTH_CALLBACK_PENDING_KEY = 'oqa_auth_cb_pending';
export const AUTH_CALLBACK_PENDING_TTL_MS = 5 * 60_000;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PendingCallback {
  google_attempt?: boolean;
  /** Epoch ms when AuthCallbackPage mounted with Clerk loaded. */
  entered_at: number;
  /** Where the callback meant to send the user; rides the completed event. */
  redirect_target: string;
}

export interface LandedCallback {
  google_attempt?: boolean;
  time_to_resolve_ms: number;
  redirect_target: string;
}

/**
 * Reading the `window.sessionStorage` PROPERTY throws SecurityError when
 * cookies are blocked or the origin is opaque, before any getItem call; the
 * same guard lib/clerkLoadRetry.ts applies for the same reason.
 */
export function sessionStorageOrNull(): StorageLike | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function writePendingCallback(store: StorageLike | null, value: PendingCallback): void {
  if (!store) return;
  try {
    store.setItem(AUTH_CALLBACK_PENDING_KEY, JSON.stringify(value));
  } catch {
    // Analytics must never break the page.
  }
}

export function clearPendingCallback(store: StorageLike | null): void {
  if (!store) return;
  try {
    store.removeItem(AUTH_CALLBACK_PENDING_KEY);
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Read AND remove the note. Returns null when there is none, when it is
 * malformed, when it is older than the TTL, or when its clock is ahead of ours
 * (a negative duration is a measurement error, not a fast callback).
 */
export function consumePendingCallback(store: StorageLike | null, now: number): LandedCallback | null {
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(AUTH_CALLBACK_PENDING_KEY);
    if (raw === null) return null;
    store.removeItem(AUTH_CALLBACK_PENDING_KEY);
  } catch {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { entered_at, redirect_target, google_attempt } = parsed as Partial<PendingCallback>;
  if (typeof entered_at !== 'number' || !Number.isFinite(entered_at)) return null;

  const elapsed = now - entered_at;
  if (elapsed < 0 || elapsed > AUTH_CALLBACK_PENDING_TTL_MS) return null;

  return {
    ...(typeof google_attempt === 'boolean' ? { google_attempt } : {}),
    time_to_resolve_ms: elapsed,
    redirect_target: typeof redirect_target === 'string' ? redirect_target : '/dashboard',
  };
}
